import React, { useState, useEffect } from "react";
import { t } from "i18next";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import { isEmpty } from "lodash";
import scanFiltersApi from "../../API/scanFilters.api";
import LogCapture from "../../utils/LogManagement/LogHelper";
import EditButton from "../../components/Buttons/EditButton";
import DeleteButton from "../../components/Buttons/DeleteButton";
import BaseActionButton from "../../components/Buttons/BaseActionButton";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import ScanFilterModal from "../../components/Modals/ScanFilterModal";
import NoResultsView from "../../components/Shared/NoResultsView";
import { useNavigate } from "react-router-dom";
import { setObject } from "../../utils/StorageHelpers";
import NestedTable from "../../components/Tables/NestedTable";
import ProductTags from "../../components/Tags/ProductTags";
import { longDateTime } from "../../utils/DateHelper";
import { COMPARE_FILTER_TYPE_SIGN } from "../../utils/constants";
import { SCANS_MANAGEMENT_ROUTES } from "../../utils/RoutesConstant";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";
import { getBooleanNullValue } from "../../utils/Helpers";

const ScanFilters = () => {
    const navigate = useNavigate();
    // Scan Filters
    const [recentFilters, setRecentFilters] = useState([]);
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sortBy, setSortBy] = useState(undefined);
    const [sortDirection, setSortDirection] = useState(undefined);
    const [deleteButton, setDeleteButton] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [filterName, setFilterName] = useState("");
    const [editFilterName, setEditFilterName] = useState(false);
    const itemsPerPage = 20;

    useEffect(() => {
        fetchRecentFilters();
    }, []);

    const fetchRecentFilters = async () => {
        setIsLoading(true);
        let result = await scanFiltersApi.listScanFilters(itemsPerPage, 0);
        setRecentFilters(result?.data);
        setRows(mapScanFilters(result?.data));
        setIsLoading(false);
    };

    const getThresholdTags = (scanFilter) => {
        const thresholdTags = [];
        scanFilter.threshold?.filters?.forEach((filter, index) => {
            thresholdTags.push(
                <span className="flex gap-1">
                    {filter.target} ({filter.unit})
                    <span className="mx-2 text-xs text-primary-color-orange">
                        {COMPARE_FILTER_TYPE_SIGN[filter.compare]}
                    </span>
                    {filter.value}
                </span>
            );
            if (scanFilter.threshold?.filters?.length !== index + 1)
                thresholdTags.push(t(scanFilter.threshold?.type));
        });
        return thresholdTags;
    };

    const getScanFiltersOptions = (scanFilter) => {
        return [
            {
                item: `${t("capture")} - ${t("startDate")}`,
                filter: scanFilter.capturedStartDate,
                type: "date",
            },
            {
                item: `${t("capture")} - ${t("endDate")}`,
                filter: scanFilter.capturedEndDate,
                type: "date",
            },
            {
                item: t("threshold"),
                filter: getThresholdTags(scanFilter),
                type: "tags",
            },
            {
                item: t("models"),
                filter: scanFilter.models,
                type: "tags",
            },
            {
                item: t("lots"),
                filter: scanFilter.lots,
                type: "tags",
            },
            {
                item: t("devices"),
                filter: scanFilter.devices,
                type: "tags",
            },
            {
                item: t("suppliers"),
                filter: scanFilter.suppliers,
                type: "tags",
            },
            {
                item: t("createdBy"),
                filter: scanFilter.createdByUsers,
                type: "tags",
            },
            {
                item: t("onBehalfOf"),
                filter: scanFilter.onBehalfOf,
                type: "tags",
            },
            {
                item: t("sampleName"),
                filter: scanFilter.sampleName,
                type: "text",
            },
            {
                item: t("note"),
                filter: scanFilter.note,
                type: "text",
            },
            {
                item: t("markedAsOutlier"),
                filter: [
                    getBooleanNullValue(scanFilter.isOutlier)?.toString(),
                ].filter((item) => item),
                type: "tags",
            },
        ];
    };

    const mapScanFilters = (data) => {
        return data && !isEmpty(data)
            ? data.map((scanFilter) => {
                  return {
                      ...scanFilter,
                      materialName: scanFilter.material?.name?.trim(),
                      lastUpdatedAt: new Date(scanFilter.lastUpdatedAt),
                      options: getScanFiltersOptions(scanFilter),
                  };
              })
            : [];
    };

    const headers = [
        { label: t("name"), name: "name", skipSort: true },
        { label: t("material"), name: "materialName", skipSort: true },
        { label: t("lastUsed"), name: "lastUpdatedAt", skipSort: true },
        { label: t("actions"), name: "actions", skipSort: true },
    ];
    const subHeaders = [
        { label: t("item"), name: "item", skipSort: true },
        { label: t("filter"), name: "filter", skipSort: true },
    ];

    const getOption = (propertyName, recentFilter) => {
        return !isEmpty(recentFilter[propertyName])
            ? recentFilter[propertyName]
            : [];
    };

    const applyRecentFilter = async (filter) => {
        const recentFilter = recentFilters.find(
            (item) => item.id === filter.id
        );
        const thresholdSample = recentFilter?.threshold || {};
        thresholdSample.filters = thresholdSample?.filters
            ?.filter((filter) => {
                return (
                    filter?.target &&
                    filter?.compare &&
                    filter?.value &&
                    filter?.unit
                );
            })
            .map((filter) => ({ ...filter, value: +filter.value }));
        setObject("scanFilter", {
            id: recentFilter.id,
            material: {
                value: recentFilter.materialId,
                label: recentFilter.material?.name,
            },
            ...(recentFilter?.capturedStartDate && {
                capturedStartDate: recentFilter?.capturedStartDate,
            }),
            ...(recentFilter?.capturedEndDate && {
                capturedEndDate: recentFilter?.capturedEndDate,
            }),
            ...(!isEmpty(thresholdSample?.filters) && {
                threshold: thresholdSample,
            }),
            ...(recentFilter?.sampleName && {
                sampleName: recentFilter?.sampleName,
            }),
            ...(recentFilter?.note && {
                note: recentFilter?.note,
            }),
            lots: getOption("lots", recentFilter),
            models: getOption("models", recentFilter),
            createdByUsers: getOption("createdByUsers", recentFilter),
            onBehalfOf: getOption("onBehalfOf", recentFilter),
            devices: getOption("devices", recentFilter),
            suppliers: getOption("suppliers", recentFilter),
            isOutlier: recentFilter?.isOutlier?.toString(),
        });
        navigate(SCANS_MANAGEMENT_ROUTES.Scans.Get);
    };

    const keys = [
        { name: "name" },
        { name: "materialName" },
        { name: "lastUpdatedAt" },
        {
            name: "actions",
            element: (row) => {
                return (
                    <div
                        className="flex nowrap justify-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DeleteButton
                            onClick={() => {
                                setDeleteButton(true);
                                setSelectedFilter(row);
                            }}
                            tippy={true}
                        />
                        <EditButton
                            onClick={() => {
                                setSelectedFilter(row);
                                setFilterName(row.name);
                                setEditFilterName(true);
                            }}
                            tippy={true}
                        />
                        <BaseActionButton
                            className="table-btn table-btn-blue ml-2"
                            onClick={() => applyRecentFilter(row)}
                            icon={"fas fa-play-circle"}
                            tippySettings={{
                                content: t("apply"),
                            }}
                        />
                    </div>
                );
            },
        },
    ];

    const subKeys = [
        { name: "item" },
        {
            name: "filter",
            element: (row) => {
                switch (row.type) {
                    case "tags":
                        return (
                            <div className="flex justify-center">
                                <ProductTags
                                    tags={row.filter}
                                    keyName="label"
                                />
                            </div>
                        );
                    case "date":
                        return (
                            <div className="flex justify-center">
                                {row.filter
                                    ? longDateTime(new Date(row.filter))
                                    : "---"}
                            </div>
                        );
                    case "text":
                        return (
                            <div className="flex justify-center">
                                {row.filter || "---"}
                            </div>
                        );
                    default:
                        break;
                }
            },
        },
    ];

    const handleDeleteScanFilter = () => {
        return new Promise((resolve, reject) => {
            scanFiltersApi
                .removeScanFilter(selectedFilter.id)
                .then((res) => {
                    if (res?.deleted) {
                        LogCapture.info(
                            "Scan filter deleted",
                            ENTITIES.scanFilters,
                            ACTIONS.scanFilters.delete
                        );
                        resolve(true);
                    } else resolve(false);
                })
                .catch((retrievedError) => {
                    LogCapture.error(
                        `Couldn't delete scan filter: ${retrievedError}`
                    );
                    reject();
                })
                .finally(() => {
                    setSelectedFilter(null);
                    fetchRecentFilters();
                });
        });
    };

    const handleEditFilterName = () => {
        return new Promise((resolve, reject) => {
            scanFiltersApi
                .updateScanFilter({
                    id: selectedFilter.id,
                    name: filterName,
                })
                .then((res) => {
                    if (res?.modified) {
                        LogCapture.info(
                            "Scan filter modified",
                            ENTITIES.scanFilters,
                            ACTIONS.scanFilters.editName
                        );
                        resolve(true);
                    } else resolve(false);
                })
                .catch((retrievedError) => {
                    LogCapture.error(
                        `Couldn't modify scan filter: ${retrievedError}`
                    );
                    reject();
                })
                .finally(() => {
                    setEditFilterName(false);
                    setSelectedFilter(null);
                    setFilterName("");
                    fetchRecentFilters();
                });
        });
    };

    return (
        <>
            <header className="main-customer-form-header main-content-header flex items-center">
                <h2 className="h2">{t("recentlyUsedFilters")}</h2>
            </header>
            <LoadingOverlay active={isLoading} text="Loading...">
                <div className="card-table shadow">
                    {!isEmpty(rows) ? (
                        <NestedTable
                            headers={headers}
                            subHeaders={subHeaders}
                            keys={keys}
                            subKeys={subKeys}
                            rows={rows}
                            subRowsKey="options"
                            isLoading={isLoading}
                            sortBy={sortBy}
                            setSortBy={setSortBy}
                            sortDirection={sortDirection}
                            setSortDirection={setSortDirection}
                            subTableColspan={4}
                        />
                    ) : (
                        <NoResultsView
                            title={t("applyFilterTitle")}
                            message={t("applyFilter")}
                        />
                    )}
                </div>
            </LoadingOverlay>

            <BaseAlertModal
                isVisible={deleteButton}
                setIsVisible={setDeleteButton}
                title={t("deleteTitle", { purpose: selectedFilter?.name })}
                message={t("deleteMessage", {
                    purpose: t("filter"),
                })}
                action={{ submitAction: handleDeleteScanFilter }}
                type="delete"
            />
            <ScanFilterModal
                isVisible={editFilterName}
                setIsVisible={setEditFilterName}
                submit={handleEditFilterName}
                filterName={filterName}
                setFilterName={setFilterName}
            />
        </>
    );
};

export default ScanFilters;
